import React, { useState, useRef, useCallback } from 'react'
import { apiUploadFile, apiPost, getErrorMessage } from '../../utils/api'

interface PreviewData {
  previewId: string
  chatNumber: number
  groupName: string
  messageCount: number
  mediaCount: number
  participantCount: number
  participants: string[]
  firstMessageDate: string | null
  lastMessageDate: string | null
  firstMessage: string | null
  firstMessageSender: string | null
  zipFilename: string
  zipFilesize: number
}

interface WhatsAppUploadWizardProps {
  isOpen: boolean
  onClose: () => void
  onImportComplete: () => void
}

type WizardStep = 'select' | 'preview' | 'importing' | 'done'

export const WhatsAppUploadWizard: React.FC<WhatsAppUploadWizardProps> = ({
  isOpen,
  onClose,
  onImportComplete,
}) => {
  const [step, setStep] = useState<WizardStep>('select')
  const [file, setFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [editGroupName, setEditGroupName] = useState('')
  const [editChatNumber, setEditChatNumber] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetWizard = useCallback(() => {
    setStep('select')
    setFile(null)
    setUploadProgress(0)
    setIsProcessing(false)
    setPreview(null)
    setEditGroupName('')
    setEditChatNumber('')
    setError(null)
    setImportResult(null)
  }, [])

  const handleClose = () => {
    resetWizard()
    onClose()
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    e.target.value = ''

    if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
      setError('Only ZIP files are accepted')
      return
    }

    setFile(selectedFile)
    setError(null)
    setIsProcessing(true)
    setUploadProgress(0)

    try {
      const data = await apiUploadFile<PreviewData>(
        '/whatsapp-archive/preview',
        selectedFile,
        (pct) => setUploadProgress(pct)
      )
      setPreview(data)
      setEditGroupName(data.groupName)
      setEditChatNumber(String(data.chatNumber))
      setStep('preview')
    } catch (err: any) {
      const msg = err?.message || 'Upload failed'
      setError(msg)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCommit = async () => {
    if (!preview) return
    setStep('importing')
    setError(null)

    try {
      const result = await apiPost<{
        message: string
        chatNumber: number
        groupName: string
        chatsImported: number
        messagesImported: number
        mediaCount: number
        participantCount: number
      }>('/whatsapp-archive/commit', {
        previewId: preview.previewId,
        chatNumber: parseInt(editChatNumber) || preview.chatNumber,
        groupName: editGroupName || preview.groupName,
      })

      setImportResult(
        `Successfully imported #${result.chatNumber} "${result.groupName}" — ${result.messagesImported.toLocaleString()} messages, ${result.mediaCount.toLocaleString()} media`
      )
      setStep('done')
      onImportComplete()
    } catch (err: any) {
      setError(getErrorMessage(err, 'Import failed'))
      setStep('preview') // Go back to preview so they can try again
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-green-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Import WhatsApp Chat</h2>
              <p className="text-xs text-gray-500">
                {step === 'select' && 'Step 1 of 3 — Select ZIP file'}
                {step === 'preview' && 'Step 2 of 3 — Review & edit details'}
                {step === 'importing' && 'Step 3 of 3 — Importing...'}
                {step === 'done' && 'Import complete'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-emerald-500 transition-all duration-500 ease-out"
            style={{
              width: step === 'select' ? '33%' : step === 'preview' ? '66%' : '100%',
            }}
          />
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          {/* Error banner */}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* STEP 1: File Selection */}
          {step === 'select' && (
            <div className="text-center py-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="hidden"
              />
              {isProcessing ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <svg className="animate-spin w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {uploadProgress < 100 ? 'Uploading...' : 'Processing ZIP...'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {file?.name} ({formatFileSize(file?.size || 0)})
                    </p>
                  </div>
                  {uploadProgress < 100 && (
                    <div className="w-48 mx-auto bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-50 border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33A3 3 0 0116.5 19.5H6.75z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Select a WhatsApp export ZIP</p>
                    <p className="text-xs text-gray-500 mt-1">
                      The file will be parsed and you can review details before importing
                    </p>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Choose File
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Preview & Edit */}
          {step === 'preview' && preview && (
            <div className="space-y-5">
              {/* Editable fields */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Group Name</label>
                  <input
                    type="text"
                    value={editGroupName}
                    onChange={(e) => setEditGroupName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Chat Number</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-400">#</span>
                    <input
                      type="number"
                      value={editChatNumber}
                      onChange={(e) => setEditChatNumber(e.target.value)}
                      className="w-32 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-blue-50 border border-blue-200/60 p-3">
                  <div className="text-lg font-bold text-blue-700">{preview.messageCount.toLocaleString()}</div>
                  <div className="text-[11px] text-blue-600/70 font-medium">Messages</div>
                </div>
                <div className="rounded-xl bg-violet-50 border border-violet-200/60 p-3">
                  <div className="text-lg font-bold text-violet-700">{preview.mediaCount.toLocaleString()}</div>
                  <div className="text-[11px] text-violet-600/70 font-medium">Media Files</div>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-200/60 p-3">
                  <div className="text-lg font-bold text-amber-700">{preview.participantCount.toLocaleString()}</div>
                  <div className="text-[11px] text-amber-600/70 font-medium">Participants</div>
                </div>
                <div className="rounded-xl bg-teal-50 border border-teal-200/60 p-3">
                  <div className="text-lg font-bold text-teal-700">{formatFileSize(preview.zipFilesize)}</div>
                  <div className="text-[11px] text-teal-600/70 font-medium">ZIP Size</div>
                </div>
              </div>

              {/* Details */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Date range</span>
                  <span className="text-gray-900 font-medium">
                    {formatDate(preview.firstMessageDate)} — {formatDate(preview.lastMessageDate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Source file</span>
                  <span className="text-gray-900 font-medium truncate ml-4 max-w-[240px]" title={preview.zipFilename}>
                    {preview.zipFilename}
                  </span>
                </div>
                {preview.firstMessage && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-gray-500 text-xs">First message</span>
                    <p className="text-gray-700 text-xs mt-1 leading-relaxed">
                      <span className="font-medium">{preview.firstMessageSender}:</span>{' '}
                      {preview.firstMessage.length > 120
                        ? preview.firstMessage.substring(0, 120) + '...'
                        : preview.firstMessage}
                    </p>
                  </div>
                )}
                {preview.participants.length > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-gray-500 text-xs">Participants</span>
                    <p className="text-gray-700 text-xs mt-1 leading-relaxed">
                      {preview.participants.slice(0, 10).join(', ')}
                      {preview.participants.length > 10 && ` +${preview.participants.length - 10} more`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Importing */}
          {step === 'importing' && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
                <svg className="animate-spin w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Importing to database...</p>
                <p className="text-xs text-gray-500 mt-1">
                  Saving #{editChatNumber} "{editGroupName}"
                </p>
              </div>
            </div>
          )}

          {/* DONE */}
          {step === 'done' && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Import Complete</p>
                {importResult && (
                  <p className="text-xs text-gray-500 mt-1">{importResult}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            {step === 'preview' && (
              <button
                onClick={() => { setStep('select'); setPreview(null); setFile(null); setError(null) }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition-colors"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step !== 'importing' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition-colors"
              >
                {step === 'done' ? 'Close' : 'Cancel'}
              </button>
            )}
            {step === 'preview' && (
              <button
                onClick={handleCommit}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Import to Database
              </button>
            )}
            {step === 'done' && (
              <button
                onClick={() => { resetWizard() }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
              >
                Import Another
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
